import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'

import { assignmentsApi } from '@/services/assignments'
import { jobsApi } from '@/services/jobs'
import { workersApi } from '@/services/workers'
import type { WorkerPayScale, WorkerSkill } from '@/services/workers'
import { SearchableSelect } from '@/components/common/searchable-select'
import { CurrencyInput } from '@/components/common/currency-input'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { getErrorMessage } from '@/lib/api'
import { getJobModificationBlockedMessage, isJobModificationBlocked } from '@/lib/job-status'
import { getAssignmentCompletionBlockedByWorkDayEntriesMessage } from '@/lib/workday-entry-status'
import {
  ASSIGNMENT_STATUSES,
  ASSIGNMENT_STATUS_LABELS,
  isTerminalAssignmentStatus,
  getAssignmentEditBlockedMessage,
  resolveAssignmentStatus,
  type AssignmentStatus,
} from '@/lib/assignment-status'
import { formatShortCurrencyLabel } from '@/lib/currency'
import { formatJobTitleWithId } from '@/components/jobs/job-title-link'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { resolveReturnToPath } from '@/lib/return-navigation'
import { useAuthStore } from '@/stores/auth-store'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'

const assignmentSchema = z.object({
  job: z.string().min(1, 'Job is required'),
  // worker: z.string().min(1, 'Worker is required'),
  worker_skill: z.string().optional(),
  pay_scale_id: z.string().optional(),
  status: z.enum(ASSIGNMENT_STATUSES).default('pending'),
  pay_type: z.enum(['per_day', 'per_hour', 'per_job', 'per_week', 'per_month']).default('per_hour'),
  pay: z.string().optional(),
  night_pay: z.string().optional(),
  holiday_pay: z.string().optional(),
  peak_pay: z.string().optional(),
  pay_flat: z.string().optional(),
  estimated_duration: z.string().optional(),
  special_pay: z.string().optional(),
})



type AssignmentFormData = z.infer<typeof assignmentSchema>
type AssignmentSaveData = AssignmentFormData & { allow_schedule_clash?: boolean }

const toOptionalNumber = (value?: string) => {
  if (!value || !value.trim()) {
    return undefined
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const getEditableStatus = (status?: string | null): AssignmentStatus => {
  return resolveAssignmentStatus(status) ?? 'pending'
}

type AssignmentPayType = 'per_day' | 'per_hour' | 'per_job' | 'per_week' | 'per_month'

const getEstimatedDurationLabel = (payType?: AssignmentPayType) => {
  switch (payType) {
    case 'per_day':
      return 'Estimated duration (days)'
    case 'per_week':
      return 'Estimated duration (weeks)'
    case 'per_month':
      return 'Estimated duration (months)'
    case 'per_job':
      return 'Estimated duration (jobs)'
    case 'per_hour':
    default:
      return 'Estimated duration (hours)'
  }
}

const isFlatAssignmentPayType = (payType?: AssignmentPayType | null) => (
  payType === 'per_job' || payType === 'per_week' || payType === 'per_month'
)

const payValueToString = (value: unknown) => (
  value === undefined || value === null || value === '' ? '' : String(value)
)

const formatWorkerSkillPayType = (skill: WorkerSkill) => {
  const payType = String(skill.pay_type_label ?? skill.pay_type ?? '').trim()
  if (!payType) {
    return ''
  }

  const normalized = payType.toLowerCase()
  if (normalized === 'per_hour' || normalized === 'hourly') return 'Hourly'
  if (normalized === 'per_day' || normalized === 'daily' || normalized === 'day') return 'Day'
  if (normalized === 'per_job' || normalized === 'job') return 'Per Job'
  if (normalized === 'per_week' || normalized === 'weekly' || normalized === 'week') return 'Per Week'
  if (normalized === 'per_month' || normalized === 'monthly' || normalized === 'month') return 'Per Month'

  return payType
}


export default function AssignmentFormPage() {

  // Job selection and queries (must be after all hooks and variables)
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEditMode = Boolean(id)
  const currentBranch = useAuthStore((state) => state.currentBranch)
  const labels = useWorkspaceLabels()
  const isEventMode = labels.mode === 'event'
  const entityLabel = labels.entity
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const searchParams = new URLSearchParams(location.search)
  const workerSkillChangedInFormRef = useRef(false)
  const [clashConfirmation, setClashConfirmation] = useState<{
    message: string
    data: AssignmentFormData
  } | null>(null)
  const prefilledJobId = searchParams.get('job') || searchParams.get('job_id') || ''
  // const prefilledWorkerId = searchParams.get('worker') || searchParams.get('worker_id') || ''
  const isJobLockedFromContext = !isEditMode && Boolean(prefilledJobId)
  const isJobFieldLocked = isJobLockedFromContext || isEditMode

  const {
    control,
    handleSubmit,
    // reset, // removed because it is unused
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<AssignmentFormData>({
    resolver: zodResolver(assignmentSchema),
    defaultValues: {
      job: prefilledJobId,
      // worker: prefilledWorkerId,
      worker_skill: '',
      pay_scale_id: '',
      status: 'pending',
      pay_type: 'per_hour',
      pay: '',
      night_pay: '',
      holiday_pay: '',
      peak_pay: '',
      pay_flat: '',
      estimated_duration: '',
      special_pay: '',
    },
  })


  const selectedJobId = watch('job')
  const selectedWorkerSkillId = watch('worker_skill')
  const selectedPayScaleId = watch('pay_scale_id')
  const selectedPayType = watch('pay_type')
  const enteredBasePay = watch('pay')

  const jobsQuery = useQuery({
    queryKey: ['jobs', labels.mode, serviceInstanceId ?? 'none'],
    queryFn: () => (isEventMode ? jobsApi.getEvents : jobsApi.getJobs)({
      page_size: 200,
    }),
    staleTime: 30_000,
  })

  const selectedJob = useMemo(() => {
    const jobs = jobsQuery.data?.results ?? []
    return jobs.find((job: any) => String(job.id) === String(selectedJobId))
  }, [jobsQuery.data, selectedJobId])

  // --- Utility functions must be defined before use ---
  const payCurrencyLabel = formatShortCurrencyLabel({
    symbol: currentBranch?.currencySymbol,
    code: currentBranch?.currencyCode,
  })

  const formatPayValue = (value?: number | string | null) => {
    if (value == null || value === '') {
      return '-'
    }

    const numeric = Number(value)
    if (!Number.isFinite(numeric)) {
      return String(value)
    }

    return numeric.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  }

  const formatPayWithCurrency = (value?: number | string | null) => {
    const amount = formatPayValue(value)
    if (amount === '-') {
      return amount
    }

    return payCurrencyLabel && payCurrencyLabel !== '-'
      ? `${amount} ${payCurrencyLabel}`
      : amount
  }

  // --- End utility functions ---

  const buildAssignmentsListPath = (fallbackJobId?: string) => {
    const params = new URLSearchParams()
    const jobId = searchParams.get('job') || searchParams.get('job_id') || fallbackJobId

    if (jobId) {
      params.set('job_id', jobId)
    }

    const basePath = isEventMode ? '/events/assignments' : '/assignments'
    return withServiceInstanceId(`${basePath}${params.toString() ? `?${params.toString()}` : ''}`, serviceInstanceId)
  }

  const returnPath = resolveReturnToPath(location.search, buildAssignmentsListPath())

  const assignmentQuery = useQuery({
    queryKey: ['assignment', id],
    queryFn: () => assignmentsApi.getAssignment(id as string),
    enabled: isEditMode,
    staleTime: 30_000,
  })

  const lockedJobId = isEditMode
    ? (assignmentQuery.data?.job_id ? String(assignmentQuery.data.job_id) : '')
    : prefilledJobId

  const lockedJobQuery = useQuery({
    queryKey: ['job', lockedJobId],
    queryFn: () => lockedJobId ? jobsApi.getJob(lockedJobId) : Promise.resolve(undefined),
    enabled: isJobFieldLocked && Boolean(lockedJobId),
    staleTime: 30_000,
  })

  const assignedWorkerId = isEditMode && assignmentQuery.data?.worker_id
    ? String(assignmentQuery.data.worker_id)
    : ''

  const assignmentSkillId = isEditMode && assignmentQuery.data?.worker_skill != null && String(assignmentQuery.data.worker_skill).trim()
    ? String(assignmentQuery.data.worker_skill)
    : ''

  const assignmentSkillLabel = isEditMode
    ? String(
        (assignmentQuery.data as { worker_skill_label?: string | null; role_label?: string | null } | undefined)?.worker_skill_label
          ?? assignmentQuery.data?.role_label
          ?? '',
      ).trim()
    : ''

  const resolvedJobId = selectedJobId || lockedJobId || prefilledJobId

  const jobAssignmentsQuery = useQuery({
    queryKey: ['job-assignments', resolvedJobId],
    queryFn: () => assignmentsApi.getAssignments({ job_id: resolvedJobId, page_size: 200 }),
    enabled: Boolean(resolvedJobId),
    staleTime: 30_000,
  })


  const lockedJobName = useMemo(() => {
    if (!isJobFieldLocked) {
      return ''
    }

    if (isEditMode) {
      const fromAssignment = String(assignmentQuery.data?.job_title ?? assignmentQuery.data?.job_ref ?? '').trim()
      if (fromAssignment) {
        return fromAssignment
      }
    }

    const fromList = String(selectedJob?.title ?? '').trim()
    if (fromList) {
      return fromList
    }

    const fromLockedQuery = String(lockedJobQuery.data?.title ?? '').trim()
    if (fromLockedQuery) {
      return fromLockedQuery
    }

    return selectedJobId ? `Job ${selectedJobId}` : 'Not available'
  }, [assignmentQuery.data?.job_ref, assignmentQuery.data?.job_title, isEditMode, isJobFieldLocked, lockedJobQuery.data?.title, selectedJob?.title, selectedJobId])

  const jobs = useMemo(() => {
    const results = jobsQuery.data?.results ?? []
    const assignment = assignmentQuery.data

    if (!assignment || !assignment.job_id || results.some((job) => String(job.id) === String(assignment.job_id))) {
      return results
    }

    return [
      {
        id: String(assignment.job_id),
        job_ref: assignment.job_ref,
        title: formatJobTitleWithId({
          jobId: assignment.job_id,
          title: assignment.job_title,
          jobRef: assignment.job_ref,
        }),
      },
      ...results,
    ]
  }, [assignmentQuery.data, jobsQuery.data?.results])
  const jobOptions = useMemo(
    () => jobs.map((job) => ({
      value: String(job.id),
      label: formatJobTitleWithId({ jobId: job.id, title: job.title, jobRef: job.job_ref }),
      keywords: `${job.title || ''} ${job.id || ''}`.trim(),
    })),
    [jobs],
  )

  // --- Worker dropdown and related queries removed ---

  const selectedJobCategoryId = useMemo(() => {
    const job = selectedJob ?? lockedJobQuery.data
    const categoryId = job?.category
      ?? (job as { category_id?: string | number | null } | undefined)?.category_id
      ?? (job as { _category_id?: string | number | null } | undefined)?._category_id
    return categoryId != null && String(categoryId).trim() ? String(categoryId) : ''
  }, [lockedJobQuery.data, selectedJob])

  const workerSkillsQueryKey = isEditMode
    ? (assignedWorkerId ? `worker-${assignedWorkerId}` : 'eligible')
    : 'eligible'

  // Fetch worker skills: all eligible skills for the job category on create/unassigned edit;
  // assigned worker's skills only when the assignment already has a worker.
  const workerSkillsQuery = useQuery({
    queryKey: ['worker-skills', workerSkillsQueryKey, selectedJobCategoryId || 'all', serviceInstanceId ?? 'none'],
    queryFn: () => {
      const params: Parameters<typeof workersApi.getWorkerSkills>[0] = {
        page_size: 200,
        service_instance_id: serviceInstanceId ?? undefined,
      }

      if (selectedJobCategoryId) {
        params.job_category = selectedJobCategoryId
      }

      if (isEditMode && assignedWorkerId) {
        params.worker = assignedWorkerId
      } else {
        params.is_active = true
      }

      return workersApi.getWorkerSkills(params)
    },
    enabled: Boolean(resolvedJobId) && (!isEditMode || assignmentQuery.isFetched),
    staleTime: 30_000,
  })


  const payScalesQuery = useQuery({
    queryKey: ['worker-pay-scales', selectedWorkerSkillId || 'none', serviceInstanceId ?? 'none'],
    queryFn: () => workersApi.getWorkerPayScales({
      worker_skill: selectedWorkerSkillId || undefined,
      is_active: true,
      page_size: 200,
      service_instance_id: serviceInstanceId ?? undefined,
    }),
    enabled: Boolean(selectedWorkerSkillId),
    staleTime: 30_000,
  })

  const selectedWorkerSkill = useMemo(
    () => (workerSkillsQuery.data ?? []).find((skill) => String(skill.id) === selectedWorkerSkillId),
    [workerSkillsQuery.data, selectedWorkerSkillId],
  )

  const assignedWorkerName = useMemo(() => {
    if (!isEditMode || !assignmentQuery.data) {
      return ''
    }

    const fromAssignment = String(assignmentQuery.data.worker_name ?? '').trim()
    if (fromAssignment) {
      return fromAssignment
    }

    const fromSkill = String(selectedWorkerSkill?.worker_display_name ?? '').trim()
    if (fromSkill) {
      return fromSkill
    }

    return assignedWorkerId ? `Worker #${assignedWorkerId}` : 'Assigned worker'
  }, [assignedWorkerId, assignmentQuery.data, isEditMode, selectedWorkerSkill?.worker_display_name])

  const assignedWorkerIds = useMemo(() => {
    const ids = new Set<string>()
    for (const assignment of jobAssignmentsQuery.data?.results ?? []) {
      if (isEditMode && assignmentQuery.data && String(assignment.id) === String(id)) {
        continue
      }
      if (assignment.worker_id) {
        ids.add(String(assignment.worker_id))
      }
    }
    return ids
  }, [jobAssignmentsQuery.data?.results, isEditMode, assignmentQuery.data, id])

  const workerSkillMatchesJobCategory = (skill: WorkerSkill) => {
    if (!selectedJobCategoryId) {
      return true
    }

    const skillCategoryId = skill.job_category
    if (skillCategoryId == null || String(skillCategoryId).trim() === '') {
      return true
    }

    return String(skillCategoryId) === selectedJobCategoryId
  }

  const workerSkillOptions = useMemo(() => {
    const options = (workerSkillsQuery.data ?? [])
      .filter((skill: WorkerSkill) => {
        if (!workerSkillMatchesJobCategory(skill) && String(skill.id) !== assignmentSkillId) {
          return false
        }

        if (isEditMode && assignedWorkerId) {
          return (
            String(skill.worker) === assignedWorkerId
            && (skill.is_active !== false || String(skill.id) === assignmentSkillId)
          )
        }

        if (isEditMode && !assignedWorkerId) {
          return (
            (skill.is_active !== false || String(skill.id) === assignmentSkillId)
            && !assignedWorkerIds.has(String(skill.worker))
          )
        }

        return !assignedWorkerIds.has(String(skill.worker))
      })
      .map((skill: WorkerSkill) => {
        const workerName = String(skill.worker_display_name ?? '').trim() || 'Worker'
        const roleTitle = String(skill.skill_role_name ?? skill.skill_role_title ?? '').trim()
        const payTypeLabel = formatWorkerSkillPayType(skill)
        const basePay = (skill as any).base_pay !== undefined && (skill as any).base_pay !== null && (skill as any).base_pay !== ''
          ? `Base Pay ${formatPayWithCurrency((skill as any).base_pay)}`
          : ''
        const showWorkerName = !isEventMode && (!isEditMode || !assignedWorkerId)
        const label = showWorkerName
          ? [workerName, roleTitle, payTypeLabel, basePay].filter(Boolean).join(' • ')
          : [roleTitle, payTypeLabel, basePay].filter(Boolean).join(' • ')
        return {
          value: String(skill.id),
          label: label || `Role #${skill.id}`,
          keywords: `${isEventMode ? '' : workerName} ${roleTitle} ${payTypeLabel} ${basePay}`.trim(),
        }
      })

    if (
      assignmentSkillId
      && !options.some((option) => option.value === assignmentSkillId)
    ) {
      options.unshift({
        value: assignmentSkillId,
        label: assignmentSkillLabel || `Skill #${assignmentSkillId}`,
        keywords: assignmentSkillLabel || assignmentSkillId,
      })
    }

    return options
  },
    [assignedWorkerId, assignedWorkerIds, assignmentSkillId, assignmentSkillLabel, formatPayWithCurrency, isEditMode, isEventMode, selectedJobCategoryId, workerSkillsQuery.data],
  )

  const selectedPayScale = useMemo(
    () => (payScalesQuery.data ?? []).find((scale: WorkerPayScale) => String(scale.id) === selectedPayScaleId),
    [payScalesQuery.data, selectedPayScaleId],
  )
  // Removed workerSelectOptions and all worker dropdown logic



  // Removed selectedWorkerDayRate and selectedWorkerHourlyRate

  // Removed effect for worker selection and related logic

  useEffect(() => {
    if (!isEditMode || !assignmentQuery.data) {
      return
    }

    const assignment = assignmentQuery.data
    reset({
      job: assignment.job_id,
      worker_skill: assignment.worker_skill ? String(assignment.worker_skill) : '',
      pay_scale_id: '',
      status: getEditableStatus(assignment.status),
      pay_type: (assignment.pay_type ?? 'per_hour') as AssignmentFormData['pay_type'],
      pay: assignment.pay != null ? String(assignment.pay) : '',
      night_pay: assignment.night_pay != null ? String(assignment.night_pay) : '',
      holiday_pay: assignment.holiday_pay != null ? String(assignment.holiday_pay) : '',
      peak_pay: assignment.peak_pay != null ? String(assignment.peak_pay) : '',
      pay_flat: assignment.pay_flat != null ? String(assignment.pay_flat) : '',
      estimated_duration: assignment.estimated_duration != null ? String(assignment.estimated_duration) : '',
      special_pay: assignment.special_pay != null ? String(assignment.special_pay) : '',
    })
  }, [assignmentQuery.data, isEditMode, reset])

  useEffect(() => {
    if (isEditMode) {
      return
    }

    const job = selectedJob ?? lockedJobQuery.data
    const hours = job?.scheduled_hours ?? (job as { estimated_hours?: number | string | null } | undefined)?.estimated_hours
    if (hours != null && hours !== '') {
      setValue('estimated_duration', String(hours))
    }
  }, [isEditMode, lockedJobQuery.data, selectedJob, setValue])

  useEffect(() => {
    if (isEditMode) {
      return
    }

    if (!selectedWorkerSkillId || assignedWorkerIds.size === 0) {
      return
    }

    const skill = (workerSkillsQuery.data ?? []).find((item) => String(item.id) === selectedWorkerSkillId)
    if (skill && assignedWorkerIds.has(String(skill.worker))) {
      setValue('worker_skill', '')
      setValue('pay_scale_id', '')
    }
  }, [assignedWorkerIds, isEditMode, selectedWorkerSkillId, setValue, workerSkillsQuery.data])

  useEffect(() => {
    if (!selectedWorkerSkillId) {
      return
    }

    if (isEditMode && !workerSkillChangedInFormRef.current) {
      return
    }

    const availablePayScales = payScalesQuery.data ?? []
    const firstPayScale = availablePayScales.find((scale) => String(scale.worker_skill ?? '') === String(selectedWorkerSkillId))
      ?? availablePayScales[0]

    if (firstPayScale) {
      if (String(selectedPayScaleId) !== String(firstPayScale.id)) {
        setValue('pay_scale_id', String(firstPayScale.id), { shouldDirty: true })
      }
      return
    }

    if (payScalesQuery.isFetched && selectedWorkerSkill) {
      setValue('pay_scale_id', '', { shouldDirty: true })
      const payType = (selectedWorkerSkill.pay_type ?? 'per_hour') as AssignmentFormData['pay_type']
      const basePay = payValueToString(selectedWorkerSkill.base_pay ?? selectedWorkerSkill.per_hour_pay_override ?? selectedWorkerSkill.day_pay_override)
      setValue('pay_type', payType, { shouldDirty: true })
      setValue('pay', basePay, { shouldDirty: true })
      setValue('night_pay', payValueToString(selectedWorkerSkill.night_pay ?? selectedWorkerSkill.night_pay_override), { shouldDirty: true })
      setValue('holiday_pay', payValueToString(selectedWorkerSkill.holiday_pay ?? selectedWorkerSkill.holiday_pay_override), { shouldDirty: true })
      setValue('peak_pay', payValueToString(selectedWorkerSkill.peak_pay), { shouldDirty: true })
      setValue('pay_flat', isFlatAssignmentPayType(payType) ? (basePay || '0') : '0', { shouldDirty: true })
      if (isEditMode) {
        setValue('special_pay', '0', { shouldDirty: true })
      }
    }
  }, [isEditMode, payScalesQuery.data, payScalesQuery.isFetched, selectedPayScaleId, selectedWorkerSkill, selectedWorkerSkillId, setValue])

  useEffect(() => {
    if (!selectedPayScale) {
      return
    }

    const payType = selectedPayScale.pay_type as AssignmentFormData['pay_type']
    const basePay = payValueToString(selectedPayScale.base_pay)
    setValue('pay_type', payType, { shouldDirty: true })
    setValue('pay', basePay, { shouldDirty: true })
    setValue('night_pay', payValueToString(selectedPayScale.night_pay), { shouldDirty: true })
    setValue('holiday_pay', payValueToString(selectedPayScale.holiday_pay), { shouldDirty: true })
    setValue('peak_pay', payValueToString(selectedPayScale.peak_pay), { shouldDirty: true })
    setValue('pay_flat', isFlatAssignmentPayType(payType) ? (basePay || '0') : '0', { shouldDirty: true })
    if (isEditMode) {
      setValue('special_pay', '0', { shouldDirty: true })
    }
  }, [isEditMode, selectedPayScale, setValue])

  useEffect(() => {
    if (isFlatAssignmentPayType(selectedPayType)) {
      const payValue = enteredBasePay
      setValue('pay_flat', payValue && payValue.trim() ? payValue : '0', { shouldDirty: true })
      return
    }

    setValue('pay_flat', '0', { shouldDirty: true })
  }, [enteredBasePay, selectedPayType, setValue])

  const saveMutation = useMutation({
    mutationFn: (data: AssignmentSaveData) => {
      const payload: Record<string, unknown> = {
        job: data.job,
        worker_skill: data.worker_skill || undefined,
        status: data.status,
        pay_type: data.pay_type,
        pay: toOptionalNumber(data.pay),
        night_pay: toOptionalNumber(data.night_pay),
        holiday_pay: toOptionalNumber(data.holiday_pay),
        peak_pay: toOptionalNumber(data.peak_pay),
        pay_flat: toOptionalNumber(data.pay_flat) ?? 0,
        estimated_duration: toOptionalNumber(data.estimated_duration),
      }

      if (data.allow_schedule_clash) {
        payload.allow_schedule_clash = true
      }

      if (isEditMode) {
        payload.special_pay = toOptionalNumber(data.special_pay) ?? 0
      }

      return isEditMode
        ? assignmentsApi.updateAssignment(id as string, payload)
        : assignmentsApi.createAssignment(payload)
    },
    onSuccess: (assignment) => {
      toast.success(isEditMode ? 'Assignment updated successfully' : 'Assignment created successfully')
      queryClient.invalidateQueries({ queryKey: ['assignments'] })
      queryClient.invalidateQueries({ queryKey: ['assignment', id] })
      if (assignmentQuery.data?.job_id) {
        queryClient.invalidateQueries({ queryKey: ['job-assignments', assignmentQuery.data.job_id] })
      }
      queryClient.invalidateQueries({ queryKey: ['job-assignments', assignment.job_id] })
      if (assignmentQuery.data?.worker_id) {
        queryClient.invalidateQueries({ queryKey: ['worker-assignments', assignmentQuery.data.worker_id] })
      }
      queryClient.invalidateQueries({ queryKey: ['worker-assignments', assignment.worker_id] })
      navigate(resolveReturnToPath(location.search, buildAssignmentsListPath(assignment.job_id)))
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      const attemptedData = saveMutation.variables
      if (
        attemptedData
        && !attemptedData.allow_schedule_clash
        && message.toLowerCase().includes('already assigned')
      ) {
        setClashConfirmation({
          message: message.replace(/^worker_skill:\s*/i, ''),
          data: attemptedData,
        })
        return
      }
      toast.error(message)
    },
  })

  const onSubmit = async (data: AssignmentFormData) => {
    if (jobModificationBlocked) {
      toast.error(jobModificationBlockedMessage || 'This job cannot be modified.')
      return
    }

    if (data.status === 'completed') {
      const jobId = data.job || assignmentQuery.data?.job_id
      const workerId = assignedWorkerId || assignmentQuery.data?.worker_id
      if (!workerId) {
        toast.error('Job assignments cannot be completed until a worker is assigned.')
        return
      }

      if (jobId && workerId) {
        try {
          const entries = await assignmentsApi.getWorkDayEntries({
            job_id: String(jobId),
            worker_id: String(workerId),
            page_size: 200,
          })
          const blockedMessage = getAssignmentCompletionBlockedByWorkDayEntriesMessage(entries.results ?? [])
          if (blockedMessage) {
            toast.error(blockedMessage)
            return
          }
        } catch (error) {
          toast.error(getErrorMessage(error))
          return
        }
      }
    }

    saveMutation.mutate(data)
  }

  const assignmentLocked = isEditMode && isTerminalAssignmentStatus(assignmentQuery.data?.status)
  const assignmentLockedMessage = getAssignmentEditBlockedMessage(assignmentQuery.data?.status)
  const jobModificationBlocked = isEditMode && isJobModificationBlocked(lockedJobQuery.data)
  const jobModificationBlockedMessage = getJobModificationBlockedMessage(lockedJobQuery.data)
  const formLocked = assignmentLocked || jobModificationBlocked
  const isPayResolving = Boolean(selectedWorkerSkillId)
    && payScalesQuery.isLoading
    && (!isEditMode || workerSkillChangedInFormRef.current)

  return (
    <div className="mx-auto w-full max-w-4xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(returnPath)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-foreground">{isEditMode ? 'Edit Assignment' : 'Create Assignment'}</h1>
            <p className="truncate text-xs text-muted-foreground">
              {isEditMode ? 'Adjust role, pay, duration, and status.' : `Select an ${entityLabel.toLowerCase()} and role.`}
            </p>
          </div>
        </div>
        {selectedJobId ? (
          <Button variant="outline" size="sm" onClick={() => navigate(`${labels.basePath}/${selectedJobId}?service_instance_id=${serviceInstanceId}`)}>
            Back to {entityLabel}
          </Button>
        ) : null}
      </div>


      {isEditMode && assignmentQuery.isLoading ? (
        <Card>
          <CardContent className="flex items-center gap-3 p-4 text-sm text-muted-foreground">
            <Spinner />
            Loading assignment details...
          </CardContent>
        </Card>
      ) : null}

      {assignmentLocked ? (
        <Card className="border-amber-200 bg-amber-50/60">
          <CardContent className="p-4 text-sm text-amber-900">
            {assignmentLockedMessage}
          </CardContent>
        </Card>
      ) : null}
      {jobModificationBlocked ? (
        <Card className="border-amber-200 bg-amber-50/60">
          <CardContent className="p-4 text-sm text-amber-900">
            {jobModificationBlockedMessage || 'This job cannot be modified.'}
          </CardContent>
        </Card>
      ) : null}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <fieldset disabled={formLocked} className="space-y-4 disabled:opacity-60">
        <Card>
          <CardHeader className="px-4 py-3">
            <CardTitle className="text-base">Assignment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 px-4 pb-4">
            <div className="grid gap-3 rounded-lg border border-border/70 bg-muted/20 p-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{entityLabel}</p>
                <p className="truncate font-medium text-foreground">{lockedJobName || selectedJobId || lockedJobId || '-'}</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{entityLabel} ID</p>
                <p className="truncate font-medium text-foreground">{selectedJobId || lockedJobId || '-'}</p>
              </div>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground">{entityLabel} Category</p>
                <p className="truncate font-medium text-foreground">
                  {lockedJobQuery.data?.category_name || selectedJob?.category_name || '-'}
                </p>
              </div>
              {!isEventMode ? (
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Primary Work</p>
                  <p className="truncate font-medium text-foreground">
                    {lockedJobQuery.data?.primary_worker_info?.name || selectedJob?.primary_worker_info?.name || '-'}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {!isJobFieldLocked ? (
                <div className="space-y-1.5 md:col-span-2 lg:col-span-3">
                  <Label htmlFor="job">{entityLabel} *</Label>
                <Controller
                  control={control}
                  name="job"
                  render={({ field }) => (
                    <SearchableSelect
                      id="job"
                      ariaLabel={entityLabel}
                      value={field.value}
                      onValueChange={field.onChange}
                      options={jobOptions}
                      placeholder={jobsQuery.isLoading ? `Loading ${entityLabel.toLowerCase()}s...` : `Select ${entityLabel.toLowerCase()}`}
                      searchPlaceholder={`Search ${entityLabel.toLowerCase()}s`}
                      emptyText={jobsQuery.isLoading ? `Loading ${entityLabel.toLowerCase()}s...` : `No ${entityLabel.toLowerCase()}s found.`}
                      disabled={jobsQuery.isLoading}
                    />
                  )}
                />
                  {errors.job ? <p className="text-xs text-destructive">{errors.job.message}</p> : null}
                </div>
              ) : null}

            {isEditMode && assignedWorkerId ? (
              <div className="space-y-1.5">
                <Label htmlFor="assigned_worker">Assigned worker</Label>
                <Input
                  id="assigned_worker"
                  value={assignedWorkerName}
                  disabled
                  readOnly
                />
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="worker_skill">Role</Label>
              <Controller
                control={control}
                name="worker_skill"
                render={({ field }) => (
                  <SearchableSelect
                    id="worker_skill"
                    ariaLabel="Role"
                    value={field.value || ''}
                    onValueChange={(value) => {
                      workerSkillChangedInFormRef.current = true
                      field.onChange(value)
                      setValue('pay_scale_id', '', { shouldDirty: true })
                    }}
                    options={workerSkillOptions}
                    placeholder={workerSkillsQuery.isLoading ? 'Loading roles...' : 'Select role'}
                    searchPlaceholder="Search roles"
                    emptyText="No roles found."
                    disabled={workerSkillsQuery.isLoading}
                  />
                )}
              />
              {!workerSkillsQuery.isLoading && !isEditMode && assignedWorkerIds.size > 0 ? (
                <p className="text-xs text-muted-foreground">
                  {isEventMode
                    ? `${assignedWorkerIds.size} roles already assigned; only available roles are listed.`
                    : `${assignedWorkerIds.size} already assigned; only unassigned workers are listed.`}
                </p>
              ) : null}
              {!workerSkillsQuery.isLoading && workerSkillOptions.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {isEventMode
                    ? (
                      isEditMode && assignedWorkerId
                        ? `No active roles found for this ${entityLabel.toLowerCase()} category.`
                        : isEditMode
                          ? `No eligible roles found for this ${entityLabel.toLowerCase()} category.`
                          : assignedWorkerIds.size > 0
                            ? `All eligible roles are already assigned to this ${entityLabel.toLowerCase()}.`
                            : `No active roles found for this ${entityLabel.toLowerCase()} category.`
                    )
                    : (
                      isEditMode && assignedWorkerId
                        ? 'No active worker skills found for the assigned worker and this job category.'
                        : isEditMode
                          ? 'No eligible worker skills found for this job category.'
                          : assignedWorkerIds.size > 0
                            ? 'All eligible workers are already assigned to this job.'
                            : 'No active worker skills found for this job category.'
                    )}
                </p>
              ) : null}
              {isEditMode && !workerSkillsQuery.isLoading && workerSkillOptions.length > 0 ? (
                <p className="text-xs text-muted-foreground">
                  {isEventMode ? 'Only roles for this assignment are listed.' : 'Only skills for the assigned worker are listed.'}
                </p>
              ) : null}
            </div>

            {isEditMode ? (
              <div className="space-y-1.5">
                <Label htmlFor="status">Status</Label>
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger id="status">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {ASSIGNMENT_STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>
                            {ASSIGNMENT_STATUS_LABELS[status]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="estimated_duration">{getEstimatedDurationLabel(selectedPayType)}</Label>
              <Controller
                control={control}
                name="estimated_duration"
                render={({ field }) => (
                  <Input
                    id="estimated_duration"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    {...field}
                  />
                )}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="pay">Base pay</Label>
              <Controller
                control={control}
                name="pay"
                render={({ field }) => <CurrencyInput id="pay" placeholder="0.00" {...field} />}
              />
            </div>
            </div>

            <div className="space-y-3 rounded-lg border border-border/70 p-3">
              <p className="text-sm font-medium text-foreground">Additional pay</p>

              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="space-y-1.5">
                  <Label htmlFor="night_pay">Night pay</Label>
                  <Controller
                    control={control}
                    name="night_pay"
                    render={({ field }) => <CurrencyInput id="night_pay" placeholder="0.00" {...field} />}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="holiday_pay">Holiday pay</Label>
                  <Controller
                    control={control}
                    name="holiday_pay"
                    render={({ field }) => <CurrencyInput id="holiday_pay" placeholder="0.00" {...field} />}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="peak_pay">Peak pay</Label>
                  <Controller
                    control={control}
                    name="peak_pay"
                    render={({ field }) => <CurrencyInput id="peak_pay" placeholder="0.00" {...field} />}
                  />
                </div>

              {isEditMode ? (
                <div className="space-y-1.5">
                  <Label htmlFor="special_pay">Special pay</Label>
                  <Controller
                    control={control}
                    name="special_pay"
                    render={({ field }) => (
                      <CurrencyInput id="special_pay" placeholder="0.00" {...field} />
                    )}
                  />
                </div>
              ) : null}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate(returnPath)}>
            Cancel
          </Button>
          <Button type="submit" disabled={saveMutation.isPending || jobsQuery.isLoading || assignmentQuery.isLoading || assignmentLocked || isPayResolving}>
            {saveMutation.isPending ? (
              <>
                <Spinner className="mr-2" />
                {isEditMode ? 'Saving...' : 'Creating...'}
              </>
            ) : isEditMode ? 'Save Assignment' : 'Create Assignment'}
          </Button>
        </div>
        </fieldset>
      </form>

      <AlertDialog
        open={Boolean(clashConfirmation)}
        onOpenChange={(open) => {
          if (!open) {
            setClashConfirmation(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Worker schedule clash</AlertDialogTitle>
            <AlertDialogDescription>
              {clashConfirmation?.message || 'This worker is already assigned to another job during the selected time slot.'}
              {' '}You can continue and resolve the clash later, or cancel and choose another worker.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saveMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={saveMutation.isPending || !clashConfirmation}
              onClick={(event) => {
                event.preventDefault()
                if (!clashConfirmation) {
                  return
                }
                saveMutation.mutate({
                  ...clashConfirmation.data,
                  allow_schedule_clash: true,
                })
                setClashConfirmation(null)
              }}
            >
              Continue anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}